import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Box,
  Button,
  TextField,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

type AcessoOutroComponente = {
  id: string;
  componente: string;
};

type Row = {
  id: string;
  componente: string;
};

type Props = {
  initialRows?: AcessoOutroComponente[];
  onAcessoOutrosComponentesChange?: (
    acessoOutrosComponentes: AcessoOutroComponente[]
  ) => void;
  currentCamadaId?: string;
};

const convertToRows = (
  acessoOutrosComponentes: AcessoOutroComponente[]
): Row[] => {
  return acessoOutrosComponentes.map((item) => ({
    id: item.id,
    componente: item.componente,
  }));
};

export const ListaAcessoOutrosComponentes: React.FC<Props> = ({
  initialRows = [],
  onAcessoOutrosComponentesChange,
  currentCamadaId,
}) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [editingValues, setEditingValues] = useState<{ componente: string }>({
    componente: '',
  });

  useEffect(() => {
    if (initialRows.length > 0) {
      setRows(convertToRows(initialRows));
    } else {
      setRows([]);
    }
  }, [initialRows]);

  const handleEdit = (row: Row) => {
    setEditingRowId(row.id);
    setEditingValues({ componente: row.componente });
  };

  const handleDelete = (id: string) => {
    const newRows = rows.filter((r) => r.id !== id);
    setRows(newRows);
    if (onAcessoOutrosComponentesChange) {
      const acessoOutrosComponentes = newRows.map((row) => ({
        id: row.id,
        componente: row.componente,
      }));
      onAcessoOutrosComponentesChange(acessoOutrosComponentes);
    }
  };

  const handleAddNew = () => {
    const newRow: Row = {
      id: String(Date.now()),
      componente: '',
    };
    const newRows = [newRow, ...rows];
    setRows(newRows);
    setEditingRowId(newRow.id);
    setEditingValues({ componente: '' });
  };

  const handleSave = (id: string) => {
    if (!editingValues.componente.trim()) {
      // Se o campo estiver vazio, remove a linha (caso seja uma nova linha)
      handleDelete(id);
      return;
    }

    const newRows = rows.map((r) =>
      r.id === id
        ? {
          ...r,
          componente: editingValues.componente,
        }
        : r
    );

    setRows(newRows);
    if (onAcessoOutrosComponentesChange) {
      const acessoOutrosComponentes = newRows.map((row) => ({
        id: row.id,
        componente: row.componente,
      }));
      onAcessoOutrosComponentesChange(acessoOutrosComponentes);
    }
    setEditingRowId(null);
    setEditingValues({ componente: '' });
  };

  const handleCancel = () => {
    // Se estamos editando uma linha vazia (nova), remove ela
    if (
      editingRowId &&
      rows.find((r) => r.id === editingRowId)?.componente === ''
    ) {
      handleDelete(editingRowId);
    }
    setEditingRowId(null);
    setEditingValues({ componente: '' });
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={handleAddNew}
          startIcon={<AddIcon />}
          sx={{ minWidth: '100px' }}
        >
          Novo
        </Button>
      </Box>

      <TableContainer component={Paper} elevation={0} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '120px' }}>Ações</TableCell>
              <TableCell>Componente</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={2}
                  align="center"
                  sx={{ py: 3, color: 'text.secondary' }}
                >
                  Nenhum acesso a componente configurado
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    {editingRowId === row.id ? (
                      <>
                        <Tooltip title="Salvar">
                          <IconButton
                            size="small"
                            onClick={() => handleSave(row.id)}
                            color="primary"
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancelar">
                          <IconButton size="small" onClick={handleCancel}>
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(row)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(row.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingRowId === row.id ? (
                      <TextField
                        fullWidth
                        size="small"
                        value={editingValues.componente}
                        onChange={(e) =>
                          setEditingValues({ componente: e.target.value })
                        }
                        placeholder="Nome do componente"
                        autoFocus
                        variant="outlined"
                      />
                    ) : (
                      row.componente
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
